import type { Destination, Fallback, Network, PayoutInput, SettingsInput, SettlementConfig } from "./types";

/** An EVM address as the API accepts it (the UnizPay wallet sends on EVM networks only). */
export const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;
/** A Solana address: a base58 public key. */
const SOLANA_ADDRESS_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
/** A TRON address: base58check, 34 characters starting with T. */
const TRON_ADDRESS_RE = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;

/** How a network writes addresses. The API checks them fully (checksum, key length) on save. */
export type AddressFamily = "evm" | "solana" | "tron";

/** The family of a network, from its chain id (`eip155:…`, `solana:…`, `tron:…`). */
export function addressFamily(network: Pick<Network, "chain_id"> | undefined): AddressFamily {
  if (network?.chain_id.startsWith("solana:")) return "solana";
  return network?.chain_id.startsWith("tron:") ? "tron" : "evm";
}

const ADDRESS_PATTERNS: Record<AddressFamily, RegExp> = {
  evm: ADDRESS_RE,
  solana: SOLANA_ADDRESS_RE,
  tron: TRON_ADDRESS_RE,
};

export function isAddressOf(family: AddressFamily, address: string): boolean {
  return ADDRESS_PATTERNS[family].test(address.trim());
}

/** Shaped like an address of any family. */
export function isAnyAddress(address: string): boolean {
  return (Object.keys(ADDRESS_PATTERNS) as AddressFamily[]).some((f) => isAddressOf(f, address));
}

/** Placeholder and format error of an address field, per family. */
export const ADDRESS_FORMAT: Record<AddressFamily, { placeholder: string; error: string }> = {
  evm: { placeholder: "0x…", error: "Enter a 0x-prefixed address (42 characters)." },
  solana: { placeholder: "Solana address", error: "Enter a Solana address (32 to 44 letters and digits)." },
  tron: { placeholder: "T…", error: "Enter a TRON address (34 characters starting with T)." },
};

/**
 * Compares addresses: EVM ones without letter case (checksummed and lowercase are the same),
 * Solana and TRON ones exactly (their case matters). Whitespace never counts.
 */
export function addressKey(address: string): string {
  const a = address.replace(/\s/g, "");
  return /^0x/i.test(a) ? a.toLowerCase() : a;
}

/** A USD amount as the API accepts it, e.g. 0 or 1.50. */
export const USD_RE = /^\d+(\.\d{1,2})?$/;

/** Payment defaults for a new configuration; all of them can be changed in Settings → Payment defaults. */
export const SETTLEMENT_DEFAULTS = {
  max_conversion_bps: 100,
  underpay_tolerance: "0",
  payment_expiry_seconds: 3600,
  fee_payer: "merchant",
  underpaid_auto_accept_days: null,
} as const;

/** "Underpaid payments" in Payment defaults: wait for a member, or accept after so many days. */
export const UNDERPAID_AUTO_ACCEPT_DAYS = [1, 3, 7, 14, 30] as const;

export function underpaidLabel(days: number | null): string {
  return days === null
    ? "Wait for you to accept them"
    : `Accept what arrived after ${days} ${days === 1 ? "day" : "days"}`;
}

/** How "who pays the fees" reads in forms. */
export const FEE_PAYER_LABELS = {
  merchant: "You: fees are taken from what you receive",
  payer: "Payer: added at checkout as a processing fee",
} as const;

/** Limits of the payment defaults and of a payment's own overrides. */
export const MAX_CONVERSION_BPS = { min: 10, max: 500 } as const;

/** The conversion budget never goes below this (MIN_CONVERSION_COST_USD in @unizpay/shared). */
export const MIN_CONVERSION_COST_USD = "0.03";

/** At most this many payout tokens per mode. */
export const MAX_PAYOUTS = 20;

export const DESTINATION_LABELS: Record<Destination, string> = {
  external: "External address",
  wallet: "UnizPay wallet",
};

/** Networks a profile can settle to, i.e. with at least one output token. */
export function settlementNetworks(networks: Network[]): Network[] {
  return networks.filter((n) => n.tokens.some((t) => t.settlement));
}

/** Output tokens a merchant may choose on a network (USDC, USDT, native ETH). */
export function outputTokens(network: Network | undefined): string[] {
  return network?.tokens.filter((t) => t.settlement).map((t) => t.token) ?? [];
}

/** USDC when the network offers it: a stable payout is what most businesses want. */
export function preferredToken(tokens: string[]): string {
  return tokens.includes("USDC") ? "USDC" : (tokens[0] ?? "");
}

/** A config's payouts as the API takes them back (e.g. to save other settings unchanged). */
export function payoutInputs(cfg: SettlementConfig): PayoutInput[] {
  return cfg.payouts.map((p) => ({
    network: p.network,
    token: p.token,
    to: p.to,
    ...(p.to === "external" && { address: p.address }),
  }));
}

/** A config's explicitly set fallbacks, as the API takes them back. */
export function fallbackInputs(cfg: SettlementConfig): SettingsInput["fallbacks"] {
  return Object.fromEntries(
    cfg.fallbacks
      .filter((f) => f.custom)
      .map((f) => [f.network, { to: f.to, ...(f.to === "external" && { address: f.address ?? "" }) }]),
  );
}

/** A fallback group: a destination, or none set (a network of another family than the payouts). */
export type FallbackGroup = Destination | "unset";

export const FALLBACK_GROUP_LABELS: Record<FallbackGroup, string> = {
  ...DESTINATION_LABELS,
  unset: "Not set",
};

/** Fallbacks grouped by destination, e.g. to show "UnizPay wallet on all networks". */
export function groupFallbacks(fallbacks: Fallback[]): Array<{ to: FallbackGroup; networks: string[] }> {
  const groups: Array<{ to: FallbackGroup; networks: string[] }> = [];
  for (const f of fallbacks) {
    const to: FallbackGroup = f.to === "external" && !f.address ? "unset" : f.to;
    const group = groups.find((g) => g.to === to);
    if (group) group.networks.push(f.network);
    else groups.push({ to, networks: [f.network] });
  }
  return groups;
}
